"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const BurnOverTimeChart = ({ detailed = false, data }) => {
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      if (detailed) {
        // Detailed view: Show individual events, sorted by date
        const sortedEvents = data
          .map((event) => {
            const burnAmount = parseFloat(event.burn_amount || 0);
            return {
              datetime: event.datetime,
              total_burn_amt: burnAmount,
              burn_type: event.burn_type,
              date: new Date(event.datetime).toISOString().split("T")[0],
            };
          })
          .filter((event) => event.total_burn_amt > 0)
          .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

        setChartData(sortedEvents);
      } else {
        // Overview: Group events by date for a stacked area chart
        const groupedData = data.reduce((acc, event) => {
          const date = new Date(event.datetime).toISOString().split("T")[0];
          if (!acc[date]) {
            acc[date] = {
              date: date,
              total_burn_amt: 0,
              ecosystem_burns: 0,
              node_rewards_burns: 0,
              event_count: 0,
            };
          }
          const burnAmount = parseFloat(event.burn_amount || 0);
          acc[date].total_burn_amt += burnAmount;
          acc[date].event_count += 1;

          if (event.burn_type === "ecosystem") {
            acc[date].ecosystem_burns += burnAmount;
          } else if (event.burn_type === "node_rewards") {
            acc[date].node_rewards_burns += burnAmount;
          }

          return acc;
        }, {});

        const chartArray = Object.values(groupedData)
          .filter((item) => item.total_burn_amt > 0)
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        setChartData(chartArray);
      }
    }
  }, [data, detailed]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(1) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const date = formatDate(label);
      
      return (
        <div className="bg-gray-900 border border-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
          <div className="font-medium">{date}</div>
          {payload.map((pld, index) => (
            <div key={index} style={{ color: pld.stroke }}>
              {pld.name}: {formatNumber(pld.value)}
            </div>
          ))}
          {payload.length > 1 && (
             <div className="font-medium mt-1 text-white">
               Total: {formatNumber(payload.reduce((sum, p) => sum + p.value, 0))}
             </div>
          )}
        </div>
      );
    }
    return null;
  };

  const yAxisDomain = [
    1,
    'auto'
  ];

  if (!data) {
    return (
      <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 bg-[#23272e] rounded w-48"></div>
          </div>
          <div className="h-64 bg-[#23272e] rounded"></div>
        </div>
      </div>
    );
  }

  const summaryStats = (() => {
    if (!chartData || chartData.length === 0) {
      return { peak: 0, min: 0, events: 0, latest: "N/A", range: "N/A" };
    }
    const amounts = chartData.map(d => d.total_burn_amt);
    const peak = Math.max(...amounts);
    const min = Math.min(...amounts.filter(a => a > 0));
    return {
      peak: formatNumber(peak),
      min: formatNumber(min),
      events: chartData.length,
      latest: formatDate(chartData[chartData.length - 1].date || chartData[chartData.length - 1].datetime),
      range: min > 0 ? `${(peak / min).toFixed(0)}x` : "N/A"
    };
  })();

  return (
    <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            {detailed ? "Detailed Burn Event Analysis" : "Daily Burn Summary"}
          </h3>
          <p className="text-sm text-gray-400">
            {detailed ? "Individual burn events over time" : "Events grouped by day for overview"}
          </p>
        </div>
        <Icon icon="ph:chart-line-up-bold" className="text-xl text-metaxseed-blue" />
      </div>

      {!Array.isArray(chartData) || chartData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 flex-grow">
          <Icon icon="ph:chart-line-duotone" className="text-4xl text-gray-600 mb-4" />
          <p className="text-gray-400">No burn data available</p>
        </div>
      ) : (
        <div className="flex flex-col flex-grow">
          {/* Chart Area */}
          <div className="flex-grow w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={chartData}
                margin={{ top: 5, right: 10, left: 10, bottom: 20 }}
              >
                <defs>
                  <linearGradient id="ecosystemGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.7}/>
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="nodeRewardsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.7}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="detailedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.7}/>
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity="0.3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  tick={{ fill: '#9ca3af', fontSize: 12 }} 
                  axisLine={{ stroke: '#374151' }} 
                  tickLine={{ stroke: '#374151' }}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                  interval="preserveStartEnd"
                />
                <YAxis
                  scale="log"
                  domain={yAxisDomain}
                  allowDataOverflow
                  tickFormatter={formatNumber}
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  axisLine={{ stroke: '#374151' }}
                  tickLine={{ stroke: '#374151' }}
                  width={80}
                />
                <Tooltip 
                  content={<CustomTooltip />} 
                  cursor={{fill: 'rgba(107, 114, 128, 0.1)'}}
                />
                {detailed ? (
                  <Area 
                    type="monotone" 
                    dataKey="total_burn_amt" 
                    name="Burn Amount"
                    stroke="#ef4444" 
                    fill="url(#detailedGradient)" 
                    strokeWidth={2}
                    dot={{ r: 2, strokeWidth: 1 }}
                    activeDot={{ r: 5 }}
                  />
                ) : (
                  <>
                    <Area 
                      type="monotone" 
                      dataKey="ecosystem_burns" 
                      name="Ecosystem"
                      stackId="1" 
                      stroke="#22c55e" 
                      fill="url(#ecosystemGradient)"
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="node_rewards_burns"
                      name="Node Rewards"
                      stackId="1"
                      stroke="#3b82f6"
                      fill="url(#nodeRewardsGradient)"
                      strokeWidth={2}
                    />
                  </>
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 text-sm mt-4">
            {detailed ? (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span className="text-gray-400">Individual Burns</span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span className="text-gray-400">Ecosystem</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span className="text-gray-400">Node Rewards</span>
                </div>
              </>
            )}
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-[#2a2f38]">
            <div className="text-center">
              <p className="text-xs text-gray-400">Peak Burn</p>
              <p className="text-sm font-semibold text-white">{summaryStats.peak}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Min Burn</p>
              <p className="text-sm font-semibold text-white">{summaryStats.min}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Total Events</p>
              <p className="text-sm font-semibold text-white">{summaryStats.events}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Latest</p>
              <p className="text-sm font-semibold text-white">{summaryStats.latest}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Range Factor</p>
              <p className="text-sm font-semibold text-yellow-400">{summaryStats.range}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BurnOverTimeChart;